import { useCallback, useRef, useState, useEffect } from 'react';

import {
  getAuthorised,
  postAuthorised,
} from 'approot/shared/api/auth/auth.http';
import { isResponseHeadOk } from 'lib/network/http';
import { AnyObject, HTTP_GET, HTTP_POST } from 'webclient.constants';

type Props = {
  downloadPath: string;
  downloadFileName: string;
  downloadMethod: typeof HTTP_POST | typeof HTTP_GET;
  onError?: () => void;
};

const useDownloadAndPrintPdf = ({
  downloadPath,
  downloadFileName,
  downloadMethod,
  onError,
}: Props) => {
  const pdfFile = useRef<Blob>(undefined);
  const pdfUrl = useRef<string>(undefined);

  const [loading, setLoading] = useState<boolean>(false);

  const openPDFFile = useCallback(() => {
    if (pdfUrl.current) {
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = pdfUrl.current;
      link.download = downloadFileName;
      document.body && document.body.appendChild(link);
      link.click();
      document.body && document.body.removeChild(link);
    }
  }, [downloadFileName]);

  const fetchPdf = useCallback(
    async (requestData: AnyObject) => {
      setLoading(true);

      const response =
        downloadMethod === HTTP_GET
          ? await getAuthorised({
              path: downloadPath,
              getFields: requestData,
              headerFields: {
                accept: 'application/pdf',
              },
            })
          : await postAuthorised({
              path: downloadPath,
              postJSON: requestData,
              headerFields: {
                accept: 'application/pdf',
              },
            });

      if (!isResponseHeadOk(response)) {
        setLoading(false);
        onError?.();
        return false;
      }

      const file = await response.blob();
      const fileType = response.headers.get('Content-Type');

      if (!file.size || !fileType || !fileType.endsWith('pdf')) {
        setLoading(false);
        onError?.();
        return false;
      }

      pdfFile.current = file;
      pdfUrl.current = URL.createObjectURL(pdfFile.current);

      setLoading(false);
      return true;
    },
    [downloadMethod, downloadPath, onError]
  );

  const handlePrintClick = useCallback(
    async (requestData: AnyObject) => {
      if (!loading) {
        await fetchPdf(requestData)
          .then(success => {
            if (success) {
              openPDFFile();
            }
          })
          .catch(() => {});
      }
    },
    [fetchPdf, loading, openPDFFile]
  );

  useEffect(() => {
    return () => {
      if (pdfUrl.current) {
        URL.revokeObjectURL(pdfUrl.current);
      }
    };
  }, []);

  return {
    handlePrintClick,
    loading,
  };
};

export default useDownloadAndPrintPdf;
